import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { format, addDays, startOfWeek, subWeeks, addWeeks, subDays, isToday } from 'date-fns';
import { IconButton } from '@mui/material';
import { ReservationDayOfWeekDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import { ko } from 'date-fns/locale';
import { Row } from 'saladsoft-primitive';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';

const WeeklyCalendar = ({ onClickDate }: { onClickDate: (date: string) => void }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(() => {
    const urlDate = searchParams.get('date');
    return urlDate ? new Date(urlDate) : new Date();
  });
  const [daysOfWeek, setDaysOfWeek] = useState<ReservationDayOfWeekDto[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>();

  const fetchDayOfWeek = (date: string) => {
    ServiceHelper.adminReservationService?.getDaysOfWeek({ date: date }).then((res) => {
      setDaysOfWeek(res.data);
    });
  };

  const handleClickDate = (date: string) => {
    setSelectedDate(date);
    onClickDate(date);
    navigate(`?date=${date}`);
  };

  const handlePrevWeek = () => {
    setCurrentDate((prev) => subWeeks(prev, 1));
    const date = daysOfWeek[0]?.dateTime
      ? format(subDays(new Date(daysOfWeek[0].dateTime), 1), 'yyyy-MM-dd')
      : '';

    fetchDayOfWeek(date);
  };

  const handleNextWeek = () => {
    setCurrentDate((prev) => addWeeks(prev, 1));
    const date = daysOfWeek[6]?.dateTime
      ? format(addDays(new Date(daysOfWeek[6].dateTime), 1), 'yyyy-MM-dd')
      : '';

    fetchDayOfWeek(date);
  };

  useEffect(() => {
    fetchDayOfWeek(format(currentDate, 'yyyy-MM-dd'));
  }, [currentDate]);

  useEffect(() => {
    if (!searchParams.get('date')) {
      const today = format(new Date(), 'yyyy-MM-dd');
      navigate(`?date=${today}`);
    }
  }, []);

  useEffect(() => {
    setSelectedDate(searchParams.get('date') ?? '');
  }, [location.search]);

  // 현재 날짜 객체 생성
  const today = new Date();
  today.setHours(0, 0, 0, 0); // 시간 부분은 제거하여 날짜만 비교

  // dayInfo의 날짜와 오늘 날짜 비교
  const isCurrentDay = (date: string) => {
    // 현재 날짜를 yyyy-mm-dd 형식으로 변환
    const todayFormatted = moment(today).format('YYYY-MM-DD');

    const result = date === todayFormatted;
    return result;
  };

  return (
    <>
      <CalendarContainer>
        <IconButton onClick={handlePrevWeek} style={{ background: '#ffffff' }}>
          <ArrowBackIcon />
        </IconButton>
        <DaysContainer>
          {daysOfWeek?.map((dayInfo, index) => (
            <DayBox
              key={index}
              isToday={isCurrentDay(dayInfo.dateTime ?? '')}
              selected={selectedDate === dayInfo.dateTime}
              onClick={() => handleClickDate(dayInfo.dateTime ?? '')}
            >
              {isCurrentDay(dayInfo.dateTime ?? '') ? <Today>TODAY</Today> : null}
              <Row gap={10}>
                <span className="day-name" style={{ fontSize: 14, color: '#000000' }}>
                  {format(new Date(dayInfo.dateTime ?? new Date()), 'yy년 MM월')} &nbsp;
                  {format(new Date(dayInfo.dateTime ?? new Date()), 'EEE', { locale: ko })}요일
                </span>
                <span className="date" style={{ fontSize: 30, fontWeight: 600, color: '#000' }}>
                  {format(new Date(dayInfo?.dateTime ?? new Date()), 'd')}
                </span>
              </Row>

              <div style={{ width: '100%' }}>
                {(() => {
                  const total = (dayInfo.arrivals ?? 0) + (dayInfo.departures ?? 0);
                  const arrivalsRatio = total > 0 ? ((dayInfo.arrivals ?? 0) / total) * 100 : 0;
                  const departuresRatio = total > 0 ? ((dayInfo.departures ?? 0) / total) * 100 : 0;

                  return (
                    <>
                      <ProgressBar ratio={arrivalsRatio} type="arrivals">
                        <ProgressText ratio={arrivalsRatio}>
                          {dayInfo.arrivals === 0 ? '입실 없음' : `입실 ${dayInfo.arrivals}`}
                        </ProgressText>
                      </ProgressBar>
                      <ProgressBar ratio={departuresRatio} type="departures">
                        <ProgressText ratio={departuresRatio}>
                          {dayInfo.departures === 0 ? '퇴실 없음' : `퇴실 ${dayInfo.departures}`}
                        </ProgressText>
                      </ProgressBar>
                    </>
                  );
                })()}
              </div>
            </DayBox>
          ))}
        </DaysContainer>
        <IconButton onClick={handleNextWeek} style={{ background: '#ffffff' }}>
          <ArrowForwardIcon />
        </IconButton>
      </CalendarContainer>
    </>
  );
};

const CalendarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background-color: #efefef;
  padding: 20px;
  height: 180px;
`;

const DaysContainer = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
`;

const Today = styled.div`
  background-color: #a50000;
  color: #ffffff;
  padding: 1px 40px;
  position: absolute;
  font-size: 12px;
  font-weight: 700;
  top: -10px;
  left: 10px;
  border-radius: 25px;
`;

const DayBox = styled.div<{ isToday: boolean; selected: boolean }>`
  cursor: pointer;
  display: flex;
  background-color: #ffffff;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  margin: 20px 0;
  height: 130px;
  /* padding: ${(props) => (props.selected ? '15px 5px' : '5px')}; */
  border: ${(props) => (props.selected ? '1px solid #a50000' : '1px solid #efefef')};
  box-shadow: ${(props) => (props.selected ? '0 0 10px 0 rgba(0, 0, 0, 0.1)' : 'none')};
  border-radius: 8px;
  flex: 1;
  padding-bottom: 5px;
  /* background-color: ${(props) => (props.isToday ? 'rebeccapurple' : 'transparent')}; */
  /* transform: ${(props) => (props.selected ? 'scaleY(1.05)' : 'scaleY(1)')}; */
  transition: all 0.2s ease;
  z-index: ${(props) => (props.selected ? '1' : '0')};
  position: relative;
  .day-name {
    font-size: 14px;
    color: #666;
  }

  .date {
    font-size: 16px;
    font-weight: bold;
    margin-top: 4px;
  }
`;

const ProgressBar = styled.div<{ ratio: number; type: 'arrivals' | 'departures' }>`
  width: 100%;
  height: 25px;
  background-color: #efefef;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  margin: 6px 0;
  min-width: 100px;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => Math.max(props.ratio, 0)}%;
    min-width: ${(props) => (props.ratio > 0 ? '60px' : '0px')};
    border-radius: 25px;
    background-color: ${(props) => (props.type === 'arrivals' ? '#3CAEA3' : '#F17F42')};
    transition: width 0.3s ease;
    color: #fff;
  }
`;

const ProgressText = styled.span<{ ratio: number }>`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: ${(props) => (props.ratio > 0 ? '#ffffff' : '#949494')};
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
`;

export default WeeklyCalendar;
